import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

const ROLES = [
  {
    label: 'Responsible person (RP)',
    text: 'Under the Regulatory Reform (Fire Safety) Order, the person responsible for fire safety in the building. Usually the building owner, employer or managing agent.',
  },
  {
    label: 'Principal accountable person (PAP)',
    text: 'Under the Building Safety Act, the accountable person with overall responsibility for the safety of a higher-risk building. Where several accountable persons exist, one is the principal.',
  },
  {
    label: 'Principal designer (PD)',
    text: 'Under the Building Regulations, the designer appointed to plan, manage and monitor the design work so the building complies with the regulations.',
  },
]

const SafetyRolesExplainer = ({ align = 'right' }) => {
  const [open, setOpen] = useState(false)
  const btnRef = useRef(null)
  const popRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onClick = e => {
      if (
        popRef.current && !popRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false)
    }
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const [coords, setCoords] = useState(null)
  useEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect()
      const margin = 12
      const popWidth = Math.min(360, window.innerWidth - 2 * margin)
      const desiredLeft = align === 'left' ? r.left : r.right - popWidth
      const left = Math.max(margin, Math.min(desiredLeft, window.innerWidth - popWidth - margin))
      setCoords({ top: r.bottom + 8, left })
    }
  }, [open, align])

  return (
    <Wrap>
      <InfoButton
        ref={btnRef}
        type='button'
        aria-label='What do these safety roles mean?'
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        <InfoOutlinedIcon style={{ fontSize: 15 }} />
        <span>What are these roles?</span>
      </InfoButton>

      {open && coords && createPortal(
        <Pop
          ref={popRef}
          role='dialog'
          aria-label='What the safety roles mean'
          style={{ top: coords.top, left: coords.left }}
        >
          <PopTitle>Key safety roles</PopTitle>
          <PopLead>
            These are the people legally accountable for this building's safety. Assign the
            person who holds each role, or invite them by email. They are recorded per building —
            each Building Passport has its own appointments.
          </PopLead>

          <RoleList>
            {ROLES.map(r => (
              <RoleRow key={r.label}>
                <RoleText><strong>{r.label}</strong>: {r.text}</RoleText>
              </RoleRow>
            ))}
          </RoleList>

          <PopTitle>Not applicable</PopTitle>
          <PopLead>
            Mark a role <strong>Not applicable</strong> when it genuinely doesn't apply to this
            building. For example, a Principal Accountable Person on a building that isn't a
            higher-risk building. This clears the reminder without assigning anyone, and you can
            change it later.
          </PopLead>
          <FootNote>
            Filling these in is recommended but won't block your compliance setup. It's a prompt,
            not a gate.
          </FootNote>
        </Pop>,
        document.body
      )}
    </Wrap>
  )
}

export default SafetyRolesExplainer

const Wrap = styled.span`
  display: inline-flex;
`

const InfoButton = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  background: rgba(255, 255, 255, 0.10);
  border: 1px solid rgba(12, 18, 41, 0.12);
  color: inherit;
  border-radius: 999px;
  padding: 4px 11px 4px 8px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 160ms ease;

  &:hover { background: rgba(12, 18, 41, 0.06); }
`

const Pop = styled.div`
  position: fixed;
  z-index: 100010;
  width: min(360px, calc(100vw - 24px));
  max-height: 70vh;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  box-shadow: 0 18px 44px -22px rgba(12, 18, 41, 0.4);
  padding: 16px 18px;
`

const PopTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
`

const PopLead = styled.p`
  margin: 0 0 12px;
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const RoleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  margin-bottom: 12px;
`

const RoleRow = styled.div`
  display: flex;
`

const RoleText = styled.span`
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const FootNote = styled.p`
  margin: 4px 0 0;
  font-size: 11px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.grey[700]};
`
